import React, { useCallback } from "react";

import { DefaultOptionType } from "antd/es/select";

import Region from "@mapmycustomers/shared/types/Region";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";
import useRegionsListOptions from "@mapmycustomers/shared/util/regions/useRegionsListOptions";

import { useConfigProvider } from "../../../../ConfigProvider";
import SelectField, { SelectFieldProps } from "../../../SelectField";
import TextField, { TextFieldProps } from "../../../TextField";

interface RegionFieldProps {
  countryCode?: null | string;
  disabled?: boolean;
  onChange?: (region: Region) => void;
  required?: boolean;
  value?: Region;
}

type SelectFieldValueType = string | undefined;

const RegionField = ({ countryCode, disabled, onChange, required, value }: RegionFieldProps) => {
  const configProvider = useConfigProvider();
  const regions = useRegionsListOptions(countryCode);
  const hasRegions = regions?.length > 0;

  const handleChange = useCallback<NonNullable<SelectFieldProps<SelectFieldValueType>["onChange"]>>(
    (value, option) => {
      onChange?.({
        region: value !== undefined ? ((option as DefaultOptionType).label as string) : undefined,
        regionCode: value,
      });
    },
    [onChange]
  );

  const handleTextChange = useCallback<NonNullable<TextFieldProps["onChange"]>>(
    (value) => {
      onChange?.({
        region: value,
        regionCode: undefined,
      });
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange?.({
      region: undefined,
      regionCode: undefined,
    });
  }, [onChange]);

  return hasRegions ? (
    <SelectField<string | undefined>
      allowClear
      dropdownMatchSelectWidth
      filterOption={defaultFilterOption}
      locked={disabled}
      onChange={handleChange}
      onClear={handleClear}
      options={regions}
      placeholder={configProvider.formatMessage("ui.manualAddress.regionProvince")}
      required={required}
      showSearch
      value={value?.regionCode}
    />
  ) : (
    <TextField
      locked={disabled}
      onChange={handleTextChange}
      placeholder={configProvider.formatMessage("ui.manualAddress.regionProvince")}
      required={required}
      value={value?.region}
    />
  );
};

export default RegionField;
